import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { DateTimeService, UIService } from '@services';

import { TranslateService } from '@ngx-translate/core';
import {
  ActiveErrorsStore,
  DevicesStore,
  InstallationDetailsStore,
  TrendingChartType,
  TrendingData,
  TrendingStore,
} from '@store';
import { hideTooltipDescriptionForMobileChartOptions } from '../../../../../shared/common/highchart-options';

import { sortBy } from 'lodash';
import { TRENDING_UNITS_ADAPTER_IT } from '../../../../../operational-data-common/services/units-adapter/units-adapter.module';
import { UnitsAdapter } from '../../../../../operational-data-common/services/units-adapter/units.adapter';
import { CustomTranslationsService } from '../../../../../operational-data-common/translations';
import { BaseTrendingChartComponent } from '../base-trending-chart.component';
import { TrendingChartLegendComponent } from '../trending-chart-legend/trending-chart-legend.component';
import { getTrendingChartLabelsPosition, TrendingSeries } from '../trending-chart.helpers';

@Component({
  selector: 'app-trending-line-chart',
  templateUrl: './trending-line-chart.component.html',
  styleUrls: ['./trending-line-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrendingLineChartComponent extends BaseTrendingChartComponent {
  @ViewChild('customLegendContainer', { read: ViewContainerRef })
  customLegendContainer: ViewContainerRef;

  unitToYAxisID: { [key: string]: number } = {};

  constructor(
    protected trendingStore: TrendingStore,
    protected cdr: ChangeDetectorRef,
    protected translateService: TranslateService,
    protected uiService: UIService,
    protected activeErrorsStore: ActiveErrorsStore,
    protected dateTimeService: DateTimeService,
    protected devicesStore: DevicesStore,
    protected installationDetailsStore: InstallationDetailsStore,
    @Inject(TRENDING_UNITS_ADAPTER_IT) protected unitsAdapter: UnitsAdapter,
    private customTranslationsService: CustomTranslationsService
  ) {
    super(
      trendingStore,
      cdr,
      translateService,
      uiService,
      activeErrorsStore,
      dateTimeService,
      devicesStore,
      installationDetailsStore,
      unitsAdapter
    );
  }

  protected createChartOptions(data: TrendingData, features: string[]): Highcharts.Options {
    const yAxis = this.generateYAxis(data);

    return {
      ...this.initChartOptions,
      ...hideTooltipDescriptionForMobileChartOptions,
      tooltip: {
        ...this.initChartOptions.tooltip,
        positioner: (labelWidth, labelHeight, point): any => {
          return getTrendingChartLabelsPosition(labelWidth, labelHeight, point, this.chart);
        },
      },
      chart: {
        ...this.initChartOptions.chart,
        events: {
          load: (): void => {
            setTimeout(() => {
              this.generateCustomLegend(this.chart);
            }, 0);
          },
        },
      },
      plotOptions: {
        ...this.initChartOptions.plotOptions,
        series: {
          ...this.initChartOptions.plotOptions.series,
          pointStart: data.point_start,
          pointInterval: data.point_interval,
        },
      },
      yAxis: yAxis,
      series: this.getChartSeries(data, features),
    };
  }

  private generateYAxis(data: TrendingData): Highcharts.YAxisOptions[] {
    this.unitToYAxisID = {};
    const featureListWithSeries = this.trendsFiltered
      .map((entry) => ({
        ...entry,
        unit: this.unitsAdapter.getUnit(entry.unit),
      }))
      .filter((entry) => {
        const series = data.series.find((set) => set?.feature === entry.feature)?.data;
        const seriesPresent = series?.length;

        return seriesPresent;
      });

    let yAxisOptions: Highcharts.YAxisOptions[] = featureListWithSeries
      .map((entry) => {
        if (!Object.keys(this.unitToYAxisID).includes(entry.unit)) {
          const index = Object.keys(this.unitToYAxisID).length;
          this.unitToYAxisID[entry.unit] = index;

          return {
            id: index.toString(),
            title: {
              text: `${this.getUnitTranslation(entry.unit)} (${this.unitsAdapter.getSuffix(
                entry.unit
              )})`,
            },
            opposite: false,
            showEmpty: false,
          };
        }
      })
      .filter((option) => !!option);

    return yAxisOptions;
  }

  private getUnitTranslation(unit: string): string {
    const translationKey = this.customTranslationsService.UNIT_TRANSLATION_LINE_CHART[unit];

    return translationKey ? this.translateService.instant(translationKey) : unit;
  }

  protected generateSeriesFromFeatures(
    data: TrendingData,
    features: string[]
  ): Highcharts.SeriesOptionsType[] {
    const seriesToCreate = [];

    data.series.forEach((entry) => {
      const matchedDataObjects = this.featureListWithSeries.find(
        (feature) => feature.feature === entry.feature
      );

      if (!matchedDataObjects) {
        return;
      }

      seriesToCreate.push({
        ...entry,
        ...matchedDataObjects,
      });
    });

    let chartSeriesOptions: Highcharts.SeriesOptionsType[] = seriesToCreate.map((entry) => {
      const type = entry.step ? TrendingChartType.Line : TrendingChartType.Spline;
      let visible = !!features.find((set) => set === entry.boilerSerialFeature);

      const dataLocalized = entry.data.map((value) => {
        return this.unitsAdapter.getValue(value, entry.unit);
      });

      return {
        deviceId: entry.deviceId,
        boilerSerial: entry.boilerSerial,
        deviceType: entry.deviceType,
        shortDeviceName: entry.shortDeviceName,
        id: entry.boilerSerialFeature,
        data: dataLocalized,
        yAxis: this.unitToYAxisID[this.unitsAdapter.getUnit(entry.unit)],
        name: entry.label,
        showInLegend: true,
        visible: visible,
        type,
        tooltip: {
          valueSuffix: this.unitsAdapter.getSuffix(entry.unit),
          valueDecimals: 2,
        },
        step: entry.step && type === 'line' ? 'center' : undefined,
      } as Highcharts.SeriesOptionsType;
    });

    return sortBy(chartSeriesOptions, 'boilerSerial', 'name');
  }

  private generateCustomLegend(context: Highcharts.Chart): void {
    this.customLegendElement = this.customLegendContainer.createComponent(
      TrendingChartLegendComponent
    );
    this.customLegendElement.instance.chart = context;
    if (this.isHasCategories) {
      const series: TrendingSeries[] = (<unknown>context.series) as TrendingSeries[];
      this.customLegendElement.instance.trendingCategories = this.trendingCategories.map(
        (trendingCategory) => {
          if (series[0].userOptions.deviceId === trendingCategory.deviceId) {
            return {
              ...trendingCategory,
              items: trendingCategory.items.map((item, index) => ({
                ...item,
                active: index === 0 ? true : false,
              })),
            };
          }

          return trendingCategory;
        }
      );
    } else {
      this.customLegendElement.instance.trendingCategories = this.trendingCategories;
    }
    this.customLegendElement.instance.redrawLegend();
  }
}
